// frontend/src/components/production-sequence/SequenceTable.tsx
import { useState, useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { exportSecuenciaExcel } from '@/utils/exportSecuenciaExcel';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TableToolbar } from '@/components/common/TableToolbar';
import { Loader } from '@/components/common/Loader';
import { useScrollToCurrentOrder } from '@/hooks/useScrollToCurrentOrder';
import { Pagination } from '@/components/common/Pagination';
import { SortArrow } from '@/components/common/SortableHeader';
import { AggregationMenu } from '@/components/common/AggregationMenu';
import { TableTotalsFooter } from '@/components/common/TableTotalsFooter';
import { useColumnAggregations } from '@/hooks/useColumnAggregations';
import { SequenceTableRow } from './SequenceTableRow';
import {
  COL_W, STICKY_OFFSETS, STICKY_TOTAL, STICKY_WIDTHS, TABLE_MIN_W, thWidth, type ColKey,
} from './sequenceLayout';
import { BarraMarcas, useMarcasFilas } from '@/components/common/MarcasFilas';
import { ColumnFilterPopover } from './ColumnFilterPopover';
import { REORDERABLE_STATUSES } from '@/types/productionSequence';
import type { ProductionOrder, SortField } from '@/types/productionSequence';
import type { AggColumn, AggFn } from '@/types';
import type { UseProductionSequenceReturn } from '@/hooks/useProductionSequence';

const PAGE_SIZE = 200;
const GROUP_H = 30; // alto (px) de la banda de grupos; sirve de offset top para la fila de cabeceras

type Kind = 'text' | 'number' | 'set';
type GroupKey = 'general' | 'servicio' | 'prevalid' | 'productividad';
// `key` es también la clave de ancho en sequenceLayout.COL_W y la de i18n
// (`sequence.col.<key>`), así que las tres cosas no pueden desalinearse.
interface ColMeta { field: SortField; key: ColKey; kind: Kind; group: GroupKey; sticky?: number; align?: 'right'; }

// Colores de banda por grupo. Sólidos (no alpha) para que tapen las filas al hacer
// scroll vertical con la cabecera fija. Variantes claro/oscuro coherentes con el tema.
const GROUP_META: Record<GroupKey, { light: string; dark: string }> = {
  general:       { light: 'bg-indigo-50 text-indigo-700', dark: 'bg-indigo-950 text-indigo-300' },
  servicio:      { light: 'bg-amber-50 text-amber-700',   dark: 'bg-amber-950 text-amber-300' },
  prevalid:      { light: 'bg-teal-50 text-teal-700',     dark: 'bg-teal-950 text-teal-300' },
  productividad: { light: 'bg-sky-50 text-sky-700',       dark: 'bg-sky-950 text-sky-300' },
};

// Orden de columnas agrupado por categoría (las 4 primeras siguen congeladas a la
// izquierda y en el mismo orden, por lo que las constantes sticky no cambian).
const COLUMNS: ColMeta[] = [
  // ── Información general ──
  { field: 'sequencePosition', key: 'position', kind: 'number', group: 'general', sticky: STICKY_OFFSETS.pos },
  { field: 'id',          key: 'ot',          kind: 'text', group: 'general', sticky: STICKY_OFFSETS.ot },
  { field: 'referencia',  key: 'referencia',  kind: 'text', group: 'general', sticky: STICKY_OFFSETS.ref },
  { field: 'descripcion', key: 'descripcion', kind: 'text', group: 'general', sticky: STICKY_OFFSETS.desc },
  { field: 'familia',     key: 'familia',     kind: 'set',  group: 'general' },
  { field: 'subfamilia',  key: 'subfamilia',  kind: 'set',  group: 'general' },
  { field: 'acabado',     key: 'acabado',     kind: 'set',  group: 'general' },
  { field: 'esReferenciaNueva', key: 'refNueva', kind: 'set', group: 'general' },
  { field: 'comentarios', key: 'comentarios', kind: 'text', group: 'general' },
  // ── Nivel de servicio ──
  { field: 'estado',      key: 'estado',      kind: 'set',  group: 'servicio' },
  { field: 'udOT',        key: 'udOT',        kind: 'number', group: 'servicio', align: 'right' },
  { field: 'udProducidas',key: 'udProd',      kind: 'number', group: 'servicio', align: 'right' },
  { field: 'udPendientes',key: 'udPte',       kind: 'number', group: 'servicio', align: 'right' },
  { field: 'adherenciaPct', key: 'adherencia', kind: 'number', group: 'servicio', align: 'right' },
  { field: 'fechaPedido', key: 'fPedido',     kind: 'text', group: 'servicio' },
  { field: 'fechaPlanificadaFin', key: 'fPlanFin', kind: 'text', group: 'servicio' },
  { field: 'fechaRealFin',key: 'fRealFin',    kind: 'text', group: 'servicio' },
  { field: 'motivoDesviacion', key: 'motivoDesv', kind: 'text', group: 'servicio' },
  // ── Estado pre-validaciones ──
  { field: 'campoCalidad',key: 'calidad',     kind: 'text', group: 'prevalid' },
  { field: 'granelStatus',key: 'granel',      kind: 'set',  group: 'prevalid' },
  { field: 'componentesStatus', key: 'componentes', kind: 'set', group: 'prevalid' },
  // ── Productividad ──
  { field: 'upm',         key: 'upm',         kind: 'number', group: 'productividad', align: 'right' },
  { field: 'udTurnoPromedio', key: 'udTurno', kind: 'number', group: 'productividad', align: 'right' },
  { field: 'nOperarios',  key: 'nOp',         kind: 'number', group: 'productividad', align: 'right' },
];

// Campos que abren un grupo nuevo (excepto el primero) → reciben separador vertical.
const GROUP_START_FIELDS = new Set<SortField>(['estado', 'campoCalidad', 'upm']);

// ── Totales de columna ────────────────────────────────────────────────────────
// La posición de secuencia es numérica pero sumarla no significa nada, así que
// queda fuera. Las columnas de ratio arrancan en media, no en suma.
const AGG_CONFIG: Record<string, { defaultAgg?: AggFn; decimals?: number }> = {
  udOT:       { defaultAgg: 'sum' },
  udProd:     { defaultAgg: 'sum' },
  udPte:      { defaultAgg: 'sum' },
  adherencia: { defaultAgg: 'avg', decimals: 1 },
  upm:        { decimals: 1 },
  udTurno:    { decimals: 1 },
  nOp:        {},
};

const AGG_COLS: AggColumn<ProductionOrder>[] = COLUMNS
  .filter(c => c.kind === 'number' && c.key in AGG_CONFIG)
  .map(c => ({ key: c.key, numeric: true, valueKey: c.field, ...AGG_CONFIG[c.key] }));

const AGG_KEYS = new Set(AGG_COLS.map(c => c.key));
// +1 por el canalón de marcado, que va congelado pero no está en COLUMNS: no es
// una columna de datos (no ordena, no filtra y no totaliza).
const STICKY_COUNT = COLUMNS.filter(c => c.sticky != null).length + 1;

// Segmentos de la banda de grupos. El grupo 'general' se parte en dos celdas: una
// sticky-left que cubre las 4 columnas congeladas (lleva la etiqueta, siempre visible)
// y otra que scrollan con el resto. Los demás grupos son una sola celda.
interface BandSeg { group: GroupKey; span: number; sticky: boolean; label: boolean }
function buildBandSegments(): BandSeg[] {
  const segs: BandSeg[] = [];
  let i = 0;
  while (i < COLUMNS.length) {
    const g = COLUMNS[i].group;
    let j = i;
    while (j < COLUMNS.length && COLUMNS[j].group === g) j++;
    const run = COLUMNS.slice(i, j);
    const stickyCount = run.filter(c => c.sticky != null).length;
    if (stickyCount > 0) {
      segs.push({ group: g, span: stickyCount, sticky: true, label: true });
      if (run.length - stickyCount > 0) segs.push({ group: g, span: run.length - stickyCount, sticky: false, label: false });
    } else {
      segs.push({ group: g, span: run.length, sticky: false, label: true });
    }
    i = j;
  }
  return segs;
}
const BAND_SEGMENTS = buildBandSegments();

export function SequenceTable({ seq, dark, header, onConfigure, onOpenCalendar }: {
  seq: UseProductionSequenceReturn; dark: boolean; header?: ReactNode; onConfigure: () => void; onOpenCalendar: () => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [openFilter, setOpenFilter] = useState<SortField | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<DOMRect | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  // Ámbito propio de la Secuencia: la tabla completa, la vista reducida de AVP y
  // las tarjetas del móvil enseñan LAS MISMAS órdenes (mismo `id`), así que las
  // tres comparten ámbito y una marca puesta en cualquiera de ellas se ve en las
  // otras. El Gantt, que enseña otro conjunto, tiene el suyo.
  const marcas = useMarcasFilas('secuencia');

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500';
  const thBase = `px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider select-none whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-500'}`;
  const btnBase = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer';
  const btnNeutral = dark ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10` : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`;
  const btnActive = `${btnBase} bg-sky-500 border-sky-500 text-white`;
  const theadBg = dark ? 'bg-slate-800' : 'bg-slate-50';
  const dividerCls = dark ? 'border-l border-white/10' : 'border-l border-slate-200';

  const rows = seq.visibleOrders;
  // Los totales van sobre todas las órdenes visibles tras filtrar, no sobre la
  // página de 200 que se está pintando.
  const agg = useColumnAggregations({ storageKey: 'secuencia', rows, columns: AGG_COLS });
  useScrollToCurrentOrder({ orders: rows, isLoading: seq.isLoading, scrollRef, pageSize: PAGE_SIZE, page, setPage, resetKey: seq.selectedLine });
  const paginate = !seq.isReorderMode;
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = paginate ? rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE) : rows;

  // Opciones para filtros tipo set, derivadas de las órdenes.
  const setOptions = useMemo(() => {
    const m: Partial<Record<SortField, { value: string; label: string }[]>> = {};
    for (const c of COLUMNS.filter(c => c.kind === 'set')) {
      const vals = [...new Set(seq.orders.map(o => String(o[c.field] ?? '')))].filter(Boolean).sort();
      m[c.field] = vals.map(v => ({ value: v, label: v }));
    }
    return m;
  }, [seq.orders]);

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (over && active.id !== over.id) seq.reorderByDragDrop(String(active.id), String(over.id));
  }

  function exportExcel() {
    // Una hoja por línea (dataset completo, en orden de secuencia) con formato.
    void exportSecuenciaExcel(seq.orders, t, t('sequence.excel.filename'));
  }

  const draggableEnabled = seq.isReorderMode;

  const surfaceBg = dark ? 'bg-slate-900' : 'bg-white';
  const table = (
    <table className={`border-collapse text-left ${surfaceBg}`} style={{ minWidth: TABLE_MIN_W }}>
        <thead>
          {/* ── Banda de grupos de columnas ── */}
          <tr>
            {BAND_SEGMENTS.map((s, si) => {
              const meta = GROUP_META[s.group];
              const color = dark ? meta.dark : meta.light;
              const newGroup = si > 0 && BAND_SEGMENTS[si - 1].group !== s.group;
              const base = `h-[30px] px-3 text-[10px] font-bold uppercase tracking-wider text-center select-none ${color} ${newGroup ? dividerCls : ''}`;
              const style = s.sticky
                ? { position: 'sticky' as const, top: 0, left: 0, width: STICKY_TOTAL, minWidth: STICKY_TOTAL, zIndex: 30 }
                : { position: 'sticky' as const, top: 0, zIndex: 20 };
              return (
                // La celda congelada cubre además el canalón de marcado (+1), que
                // no está en COLUMNS pero sí ocupa una columna de la rejilla.
                <th key={`band-${si}`} colSpan={s.sticky ? s.span + 1 : s.span} className={base} style={style}>
                  {s.label ? t(`sequence.group.${s.group}`) : ''}
                </th>
              );
            })}
          </tr>
          {/* ── Cabeceras de columna ── */}
          <tr>
            {/* Canalón de marcado. No usa `CabeceraMarca` porque esta cabecera es
                sticky en los dos ejes y necesita `style` propio (top de la banda
                de grupos, left 0 y el z del bloque congelado). */}
            <th
              scope="col"
              // Sin `thBase`: su `px-2` pelearía con el `px-1` del canalón.
              className={`${theadBg} px-1 select-none`}
              style={{ position: 'sticky', top: GROUP_H, left: STICKY_OFFSETS.marca, width: STICKY_WIDTHS.marca, minWidth: STICKY_WIDTHS.marca, maxWidth: STICKY_WIDTHS.marca, zIndex: 30 }}
            >
              <span className="sr-only">{t('common.marcas.columna')}</span>
            </th>
            {COLUMNS.map(c => {
              const filter = seq.columnFilters.find(f => f.field === c.field);
              const sortDir = seq.activeSort?.field === c.field ? seq.activeSort.direction : null;
              const isSticky = c.sticky != null;
              const thStyle = {
                position: 'sticky' as const,
                top: GROUP_H,
                ...thWidth(c.key),
                ...(isSticky ? { left: c.sticky, maxWidth: COL_W[c.key] } : {}),
                zIndex: isSticky ? 30 : 20,
              };
              const divider = GROUP_START_FIELDS.has(c.field) ? dividerCls : '';
              return (
                <th key={c.key} className={`group ${thBase} ${theadBg} relative ${c.align === 'right' ? 'text-right' : ''} ${divider}`} style={thStyle}>
                  <span className="inline-flex items-center gap-1">
                    {t(`sequence.col.${c.key}`)}
                    <SortArrow active={!!sortDir} dir={sortDir ?? 'asc'} dark={dark} />
                    {AGG_KEYS.has(c.key) && (
                      <AggregationMenu
                        dark={dark}
                        colLabel={t(`sequence.col.${c.key}`)}
                        active={agg.aggState[c.key] ?? null}
                        onChange={fn => agg.setAgg(c.key, fn)}
                      />
                    )}
                  </span>
                  {/* Embudo de filtro FUERA DEL FLUJO: en línea fijaba ~18px de ancho
                      en cada una de las 25 columnas. Aparece al pasar el ratón por la
                      cabecera y se queda fijo si la columna tiene filtro aplicado.
                      Lleva el fondo de la cabecera para que se lea sobre la etiqueta.
                      Se coloca en el borde contrario a la alineación del rótulo. */}
                  <button
                    onClick={e => {
                      const next = openFilter === c.field ? null : c.field;
                      setOpenFilter(next);
                      setFilterAnchor(next ? e.currentTarget.getBoundingClientRect() : null);
                    }}
                    className={`absolute top-1/2 -translate-y-1/2 ${c.align === 'right' ? 'left-0.5' : 'right-0.5'} p-0.5 rounded cursor-pointer transition-opacity ${theadBg} ${
                      filter
                        ? 'text-sky-500'
                        : `opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${dark ? 'text-slate-500 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'}`
                    }`}
                    aria-label="filter"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 2h10L7 7v3L5 11V7L1 2z"/></svg>
                  </button>
                  {openFilter === c.field && (
                    <ColumnFilterPopover
                      dark={dark}
                      colLabel={t(`sequence.col.${c.key}`)}
                      kind={c.kind}
                      currentValue={filter?.value ?? ''}
                      options={setOptions[c.field] ?? []}
                      sortDir={sortDir}
                      anchorRect={filterAnchor}
                      onApply={v => seq.setColumnFilter(c.field, c.kind, v)}
                      onClear={() => seq.clearColumnFilter(c.field)}
                      onSort={dir => seq.setColumnSort(c.field, dir)}
                      onClose={() => setOpenFilter(null)}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length + 1} className={`px-3 py-12 text-center text-[13px] ${mutedTxt}`}>
                {seq.isLoading
                  ? <Loader />
                  : t('sequence.noResultsSub')}
              </td>
            </tr>
          ) : pageRows.map(o => (
            <SequenceTableRow
              key={o.id}
              order={o}
              dark={dark}
              marcas={marcas}
              draggable={draggableEnabled && REORDERABLE_STATUSES.has(o.estado)}
              selected={seq.selectedOrderId === o.id}
              atRisk={seq.ordersAtRisk.has(o.id)}
              onSelect={() => seq.selectOrder(seq.selectedOrderId === o.id ? null : o.id)}
              pending={field => seq.pendingSync.has(`${o.id}:${field}`)}
              onEdit={(field, value) => seq.updateEditableField(o.id, field, value)}
            />
          ))}
        </tbody>
        {pageRows.length > 0 && (
          <TableTotalsFooter
            dark={dark}
            columns={[
              // El canalón de marcado no totaliza nada, pero ocupa columna: sin
              // él la rejilla del pie iría desplazada una celda a la izquierda.
              { key: '__marca__', stickyLeft: STICKY_OFFSETS.marca, width: STICKY_WIDTHS.marca },
              ...COLUMNS.map(c => ({
                key: c.key,
                decimals: AGG_CONFIG[c.key]?.decimals ?? 0,
                align: (c.align === 'right' ? 'right' : 'left') as 'right' | 'left',
                stickyLeft: c.sticky,
                width: COL_W[c.key],
              })),
            ]}
            results={agg.results}
            rowCount={rows.length}
            stickyLabel={{ span: STICKY_COUNT, width: STICKY_TOTAL }}
          />
        )}
      </table>
  );

  // Zona scrollable de la tabla (vertical + horizontal).
  // La cabecera (título + KPIs) va dentro del scroll y se aparta al desplazarse,
  // recuperando alto para la tabla. La barra de herramientas queda fija fuera del scroll.
  const scroller = (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto seq-scroll">
        {header && <div className="sticky left-0 px-4 pt-4 pb-4">{header}</div>}
        {table}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Barra de herramientas FIJA arriba (fuera del scroll): nº de órdenes + Exportar
          siempre visibles, sin importar el scroll vertical/horizontal de la tabla. */}
      <div className="shrink-0">
        <TableToolbar
          dark={dark}
          left={<span className={`text-[11px] font-semibold ${mutedTxt}`}>{rows.length.toLocaleString('es-ES')} {t('sequence.rowsLabel')}</span>}
          right={
            <>
              <button onClick={() => seq.setReorderMode(!seq.isReorderMode)} className={seq.isReorderMode ? btnActive : btnNeutral}>
                {t('sequence.reorderMode')}
              </button>
              <button onClick={onOpenCalendar} className={btnNeutral} title={t('sequence.calendar.title')}>
                <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M2 6h12M5 1.5v3M11 1.5v3"/></svg>
                {t('sequence.calendar.openBtn')}
              </button>
              <button onClick={onConfigure} className={btnNeutral}>
                <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4"/></svg>
                {t('sequence.configure')}
              </button>
              <button onClick={exportExcel} className={btnNeutral} title={t('common.exportExcel')}>
                <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h10M8 3v7M5 7l3 3 3-3"/></svg>
                {t('common.exportExcel')}
              </button>
            </>
          }
        />
      </div>

      <BarraMarcas dark={dark} marcas={marcas} className="shrink-0" />

      {draggableEnabled ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={pageRows.map(o => o.id)} strategy={verticalListSortingStrategy}>
            {scroller}
          </SortableContext>
        </DndContext>
      ) : scroller}

      {/* Paginación FIJA abajo (fuera del scroll), siempre visible. */}
      {paginate && totalPages > 1 && (
        <div className="shrink-0">
          <Pagination
            dark={dark} page={safePage} totalPages={totalPages} totalItems={rows.length}
            itemLabel={t('sequence.rowsLabel')} pageLabel={t('common.page')} ofLabel={t('common.of')}
            onFirst={() => setPage(1)} onPrev={() => setPage(safePage - 1)}
            onNext={() => setPage(safePage + 1)} onLast={() => setPage(totalPages)}
          />
        </div>
      )}
    </div>
  );
}
